import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBdRevision, useBdRevisions } from '../../../hooks/useBasicDesign.js';
import { formatLocalYmdHm } from '../../../lib/date-utils.js';
import { BdRowsView } from './BdRowsView.js';
import type { BdArtifactDef } from '../../../lib/api/basic-design.js';

export interface BdHistoryProps {
  /** 基本設計のチケット */
  bdKey: string;
  /** 節の定義 */
  def: BdArtifactDef;
}

/**
 * 節の保存の履歴。開いたときだけ読み、版・日時・保存した人・行数を新しい順に並べる。
 * 版を選ぶと、その版の行を読む形で出す（戻すのは編集して保存し直す。版を直接書き戻す口は無い）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の節の保存の履歴（版・日時・保存した人・行数）と過去の版の行を読む
 * @graph-connects useBdRevisions [calls] 履歴の取得
 * @graph-connects useBdRevision [calls] 過去の版の取得
 * @graph-connects BdRowsView [calls] 過去の版の行を読む
 */
export function BdHistory({ bdKey, def }: BdHistoryProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [version, setVersion] = useState<number | null>(null);
  const revisions = useBdRevisions(bdKey, def.key, open);
  const revision = useBdRevision(bdKey, def.key, version);

  return (
    <details
      className="rounded-lg border border-[var(--color-border)]"
      onToggle={(e) => {
        setOpen(e.currentTarget.open);
      }}
    >
      <summary className="cursor-pointer px-4 py-2 text-xs font-medium text-[var(--color-text-secondary)]">
        {t('bd.history.title')}
      </summary>
      {open ? (
        <div className="space-y-3 border-t border-[var(--color-border)] px-4 py-3 text-sm">
          {revisions.error !== null ? (
            <p className="text-[var(--color-error-text)]">{t('bd.history.failed')}</p>
          ) : revisions.data === undefined ? (
            <p className="text-[var(--color-text-muted)]">{t('bd.loading')}</p>
          ) : revisions.data.items.length === 0 ? (
            <p className="text-[var(--color-text-muted)]">{t('bd.history.empty')}</p>
          ) : (
            <ol aria-label={t('bd.history.listAria', { title: def.title })} className="space-y-1">
              {revisions.data.items.map((item) => (
                <li key={item.version} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <button
                    type="button"
                    className="font-medium text-[var(--color-accent-primary)] hover:underline"
                    aria-pressed={version === item.version}
                    onClick={() => {
                      setVersion((v) => (v === item.version ? null : item.version));
                    }}
                  >
                    {t('bd.history.version', { version: item.version })}
                  </button>
                  <span className="tabular-nums text-[var(--color-text-secondary)]">
                    {formatLocalYmdHm(item.savedAt)}
                  </span>
                  <span className="text-[var(--color-text-secondary)]">{item.savedBy}</span>
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {item.rowCount === 0
                      ? t('bd.status.notApplicable')
                      : t('bd.status.rows', { count: item.rowCount })}
                  </span>
                </li>
              ))}
            </ol>
          )}
          {version === null ? null : revision.error !== null ? (
            <p className="text-[var(--color-error-text)]">{t('bd.history.versionFailed')}</p>
          ) : revision.data === undefined ? (
            <p className="text-[var(--color-text-muted)]">{t('bd.loading')}</p>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-[var(--color-text-secondary)]">
                {t('bd.history.showing', { version: revision.data.version })}
              </p>
              {revision.data.rows.length === 0 ? (
                <p className="text-[var(--color-text-muted)]">{t('bd.status.notApplicable')}</p>
              ) : (
                <BdRowsView def={def} rows={revision.data.rows} />
              )}
            </div>
          )}
        </div>
      ) : null}
    </details>
  );
}
